/**
 * App Providers
 *
 * Shared client-side providers for theme, auth, analytics, and query
 * caching.
 *
 * Primary responsibilities:
 * - Mount app-wide client providers
 * - Scope TanStack Query cache to the active auth user
 *
 * Dependencies:
 * - next-themes
 * - @tanstack/react-query
 * - context/auth
 */

'use client'

import { FC, useEffect, useRef, useState, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { AmplitudeProvider } from '@/context/AmplitudeProvider'
import { AuthProvider } from '@/context/auth'
import { useAuth } from '@/context/auth'
import { PostHogProvider } from '@/context/PostHogProvider'
import { identifyPostHogUser } from '@/lib/analytics/posthog-client-identity'
import { buildPostHogIdentityProperties } from '@/lib/analytics/posthog-identity'
import { readSelectedWorkspaceIdFromStorage } from '@/lib/workspaces/selected-workspace-storage'
import { ThemeProvider as NextThemesProvider } from 'next-themes'
import { ThemeProviderProps } from 'next-themes/dist/types'

import { useRootMountMarker } from '@/components/utility/root-mount-marker'
import { TooltipProvider } from '@/components/ui/tooltip'
import { SurveyPopupProvider } from '@/components/surveys/survey-popup-provider'
import { DiscoverabilityProvider } from '@/components/discoverability/discoverability-provider'
import { LosiGamesProvider } from '@/components/losi-games/losi-games-provider'

const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 30 * 1000
      }
    }
  })

const hasAuthHintCookie = () => {
  if (typeof document === 'undefined') {
    return false
  }

  return document.cookie.split(';').some((cookie) => cookie.trim() === 'isLoggedIn=true')
}

const AppQueryProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth()
  const [queryClient] = useState(createQueryClient)
  const previousUserIdRef = useRef<string | null>(null)

  useRootMountMarker('app-query-provider')

  useEffect(() => {
    const nextUserId = user?.id ?? null

    if (previousUserIdRef.current === nextUserId) {
      return
    }

    if (previousUserIdRef.current !== null && nextUserId === null && hasAuthHintCookie()) {
      return
    }

    if (previousUserIdRef.current !== null) {
      queryClient.clear()
    }

    previousUserIdRef.current = nextUserId
  }, [queryClient, user?.id])

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}

const PostHogIdentity = () => {
  const { user, profile } = useAuth()
  const previousIdentityKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const nextUserId = user?.id ?? null

    if (!nextUserId) {
      if (previousIdentityKeyRef.current) {
        posthog.reset()
      }
      previousIdentityKeyRef.current = null
      return
    }

    const workspaceId = readSelectedWorkspaceIdFromStorage(nextUserId)
    const identityInput = {
      userId: nextUserId,
      email: user?.email,
      plan: profile?.plan,
      createdAt: user?.created_at,
      workspaceId
    }
    const identityProperties = buildPostHogIdentityProperties(identityInput)
    const identityKey = JSON.stringify([nextUserId, identityProperties])

    if (previousIdentityKeyRef.current === identityKey) {
      return
    }

    const identify = () => {
      identifyPostHogUser(identityInput)
    }

    identify()
    const retryId = window.setTimeout(identify, 3500)
    previousIdentityKeyRef.current = identityKey

    return () => window.clearTimeout(retryId)
  }, [profile?.plan, user?.email, user?.id])

  return null
}

export const Providers: FC<ThemeProviderProps> = ({ children, ...props }) => {
  useRootMountMarker('providers')

  const appTree = (
    <TooltipProvider>
      <AuthProvider forceLogin={true}>
        <AppQueryProvider>
          <PostHogIdentity />
          <SurveyPopupProvider />
          <DiscoverabilityProvider>
            <LosiGamesProvider>
              <AmplitudeProvider>{children}</AmplitudeProvider>
            </LosiGamesProvider>
          </DiscoverabilityProvider>
        </AppQueryProvider>
      </AuthProvider>
    </TooltipProvider>
  )

  return (
    <NextThemesProvider {...props} enableSystem={true} enableColorScheme={true} storageKey="theme">
      <PostHogProvider>{appTree}</PostHogProvider>
    </NextThemesProvider>
  )
}
